/**
 * Auth state (zustand store).
 * Surface area kept tiny on purpose — pages call useAuth() and route on
 * the resolved state; nothing else outside the auth shell touches it.
 */
import { create } from 'zustand';

import type { AdminUser } from '@domain/auth/AdminUser';
import { resolveRoute } from '@domain/auth/AdminUser';
import { apiClient, UNAUTHENTICATED_EVENT } from '@infrastructure/api/client';
import { errorText } from '@infrastructure/api/errors';
import { AuthRepository } from '@infrastructure/auth/AuthRepository';

interface AuthState {
  user: AdminUser | null;
  loading: boolean;
  error: string | null;
  /** Set between the password and the code step (two-step sign-in, 2026-10-29). */
  mfaToken: string | null;
  bootstrap: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  verifyCode: (code: string) => Promise<void>;
  cancelMfa: () => void;
  logout: () => Promise<void>;
  setUser: (u: AdminUser | null) => void;
}

const repo = new AuthRepository(apiClient);

export const useAuth = create<AuthState>((set, get) => ({
  user: null,
  loading: true,
  error: null,
  mfaToken: null,
  setUser: (u) => set({ user: u }),
  bootstrap: async () => {
    set({ loading: true, error: null });
    try {
      const u = await repo.me();
      set({ user: u, loading: false });
    } catch {
      set({ user: null, loading: false });
    }
  },
  login: async (email: string, password: string) => {
    set({ loading: true, error: null });
    try {
      const { mfaToken } = await repo.login(email, password);
      set({ mfaToken, loading: false });
    } catch (e) {
      set({
        loading: false,
        // The backend's own message ("Wrong email or password…", lockout, rate
        // limit), not axios' "Request failed with status code 400"
        error: errorText(e) || 'Sign-in failed. Try again.',
      });
      throw e;
    }
  },
  verifyCode: async (code: string) => {
    const token = get().mfaToken;
    if (!token) return;
    set({ loading: true, error: null });
    try {
      const u = await repo.verifyCode(token, code);
      set({ user: u, mfaToken: null, loading: false });
    } catch (e) {
      set({ loading: false, error: errorText(e) || 'Sign-in failed. Try again.' });
      throw e;
    }
  },
  cancelMfa: () => set({ mfaToken: null, error: null }),
  logout: async () => {
    try {
      await repo.logout();
    } finally {
      set({ user: null });
    }
  },
}));

if (typeof window !== 'undefined') {
  window.addEventListener(UNAUTHENTICATED_EVENT, () => {
    useAuth.getState().setUser(null);
  });
}

export { resolveRoute };
